import { assign } from 'fsm';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Raven from 'raven-js';
import { getObjectType, getObjectTypeLabel, isContactObjectType, wait } from '../../utils';
import { enrichRecords } from './client';
import { usageTracker } from '../../../usageTracker';
import { ErrorAlertMessage } from '../../ErrorAlertMessage';
import { getEnrichmentTypeFromObjectTypeId } from '../../../utils/getEnrichmentTypeFromEnrichmentId';
import { jsx as _jsx } from "react/jsx-runtime";
export const machine = {
  initial: 'loadSettings',
  states: {
    loadSettings: {
      always: [{
        target: 'idle',
        actions: ['setEnrichmentType', 'trackOpenModal', 'setCount']
      }],
      tags: ['loading']
    },
    done: {},
    idle: {
      on: {
        ENRICH: 'enriching',
        SET_OVERWRITE: {
          target: 'idle',
          actions: ['setOverwrite']
        }
      }
    },
    enriching: {
      invoke: {
        src: 'enrichRecords',
        onDone: {
          target: 'enrichedRecords',
          actions: ['logEnrichment', 'addEnrichSuccess', 'trackEnrichedRecords']
        },
        onError: {
          target: 'errorEnrichment',
          actions: ['setErrorId', 'addEnrichError', 'logError']
        }
      },
      tags: ['loading']
    },
    enrichedRecords: {
      invoke: {
        src: 'wait',
        onDone: {
          target: 'done',
          actions: ['closeModal']
        }
      }
    },
    error: {
      on: {
        ENRICH: 'enriching',
        SET_OVERWRITE: {
          actions: ['setOverwrite']
        }
      }
    },
    errorEnrichment: {
      on: {
        ENRICH: 'enriching'
      }
    },
    errorUsage: {
      on: {
        RETRY: 'loadingCredits'
      }
    }
  },
  on: {
    TOGGLE_ENRICH_ASSOCIATED_RECORDS: {
      actions: ['toggleEnrichAssociations']
    }
  },
  actions: {
    addEnrichError: context => {
      const {
        addAlertDanger,
        companyLabelPluralLowercase,
        contactLabelPluralLowercase,
        errorId,
        objectTypeId
      } = context;
      const objectTypeLabel = isContactObjectType(objectTypeId) ? contactLabelPluralLowercase : companyLabelPluralLowercase;
      addAlertDanger === null || addAlertDanger === void 0 || addAlertDanger({
        message: /*#__PURE__*/_jsx(ErrorAlertMessage, {
          errorId: errorId,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "enrichmentProperties.modals.enrichRecords.alerts.error.description",
            options: {
              objectType: objectTypeLabel
            }
          })
        }),
        titleText: I18n.text('enrichmentProperties.modals.enrichRecords.alerts.error.title', {
          objectType: objectTypeLabel
        })
      });
    },
    addEnrichSuccess: context => {
      const {
        addAlertSuccess,
        companyLabelLowercase,
        companyLabelPluralLowercase,
        contactLabelLowercase,
        contactLabelPluralLowercase,
        count,
        objectTypeId
      } = context;
      const labels = {
        companyLabelLowercase,
        companyLabelPluralLowercase,
        contactLabelLowercase,
        contactLabelPluralLowercase
      };
      const objectTypeLabel = getObjectTypeLabel(objectTypeId, count, labels);
      addAlertSuccess === null || addAlertSuccess === void 0 || addAlertSuccess({
        message: I18n.text('enrichmentProperties.modals.enrichRecords.alerts.success.description', {
          count,
          objectType: objectTypeLabel
        }),
        titleText: I18n.text('enrichmentProperties.modals.enrichRecords.alerts.success.title')
      });
    },
    closeModal: context => {
      var _context$onDone;
      (_context$onDone = context.onDone) === null || _context$onDone === void 0 || _context$onDone.call(context);
    },
    logEnrichment: context => {
      var _context$onLogging;
      (_context$onLogging = context.onLogging) === null || _context$onLogging === void 0 || _context$onLogging.call(context, {
        selectionIds: context.selectionIds
      });
    },
    logError: (_context, event) => {
      const {
        responseJSON
      } = event.data;
      Raven.captureException(responseJSON);
    },
    setCount: assign(context => {
      const {
        selectionIds
      } = context;
      const count = selectionIds.length;
      return {
        count
      };
    }),
    setErrorId: assign((_context, event) => {
      const {
        responseJSON
      } = event.data;
      const {
        correlationId
      } = responseJSON;
      return {
        errorId: correlationId
      };
    }),
    setEnrichmentType: assign(context => {
      return {
        enrichmentType: getEnrichmentTypeFromObjectTypeId(context.objectTypeId)
      };
    }),
    setOverwrite: assign((_context, event) => {
      return {
        shouldOverwrite: event.data.shouldOverwrite
      };
    }),
    toggleEnrichAssociations: assign(context => {
      return {
        enrichAssociations: !context.enrichAssociations
      };
    }),
    trackEnrichedRecords: context => {
      usageTracker.track('enriched-record', {
        objectType: getObjectType(context.objectTypeId),
        enrichmentType: 'bulk',
        selectionType: 'manual',
        includeAssociatedRecords: context.enrichAssociations
      });
    },
    trackOpenModal: context => {
      usageTracker.track('interaction', {
        screen: 'enrichment-bulk-modal',
        subscreen: getObjectType(context.objectTypeId),
        action: 'opened-enrichment-modal'
      });
    }
  },
  guards: {},
  services: {
    enrichRecords,
    wait: async () => {
      await wait(500);
    }
  }
};